// apps/web/src/components/dashboard/DashboardRail.tsx
//
// KAN-209 Briefing Desk — right rail. Three panels backed entirely by
// existing data:
//   · Rising signals — stories with ≥2 corroborating sources, ordered by
//     most recent source addition (an honest "recently corroborated"
//     signal; no invented velocity metric).
//   · Regulatory watch — the T8 /api/dashboard/regulatory-tile contract.
//   · Top victim countries — the T7 endpoint with its sector fallback.
//
// Source health was considered and rejected: /api/sources is admin-only.

import { useId, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { useDashboardTile } from '@/hooks/useDashboardTile'
import { displayMapper } from '@/lib/display-mapper'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import type { RegulatoryTileResponse } from '@/hooks/useRegulatoryQueries'
import type { Severity, StoryCardShape } from '@/types/story'

const RISING_LIMIT = 5
const DEADLINE_LIMIT = 4
const VICTIM_LIMIT = 5

interface T7Response {
  sufficient: boolean
  buckets: Array<{ countryIso2: string; count: number }>
  fallbackSectors: Array<{ sector: string; count: number }> | null
}

const SEVERITY_DOT_CLASSES: Record<Severity, string> = {
  critical: 'bg-severity-critical',
  high: 'bg-severity-high',
  medium: 'bg-severity-medium',
  low: 'bg-severity-low',
}

function RailPanel(props: { label: string; children: ReactNode }) {
  const headingId = useId()
  return (
    <section
      aria-labelledby={headingId}
      className="rounded-lg border border-border bg-card p-4"
    >
      <h3
        id={headingId}
        className="text-[11px] font-medium uppercase tracking-[0.15em] text-muted-foreground"
      >
        {props.label}
      </h3>
      <div className="mt-3">{props.children}</div>
    </section>
  )
}

function PanelSkeleton(props: { label: string }) {
  return (
    <div role="status" aria-label={`Loading ${props.label}`}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="mb-2.5 h-3.5 w-full last:mb-0" />
      ))}
    </div>
  )
}

// Deliberately NOT role="alert": the feed owns the page's single alert
// region; three rail panels erroring at once must not stack live regions.
function PanelError(props: { what: string }) {
  return (
    <p className="py-2 text-sm text-muted-foreground">
      Unable to load {props.what}.
    </p>
  )
}

function PanelEmpty(props: { children: ReactNode }) {
  return <p className="py-2 text-sm text-muted-foreground">{props.children}</p>
}

// ─── Rising signals ───────────────────────────────────────────────────────────

function RisingSignalsPanel(props: {
  stories: StoryCardShape[] | undefined
  isLoading?: boolean
  isError?: boolean
}) {
  let body: ReactNode
  if (props.isLoading) {
    body = <PanelSkeleton label="rising signals" />
  } else if (props.isError) {
    body = <PanelError what="rising signals" />
  } else {
    const rising = [...(props.stories ?? [])]
      .filter((s) => s.sourceCount >= 2)
      .sort(
        (a, b) =>
          new Date(b.latestSourceAddedAt).getTime() -
          new Date(a.latestSourceAddedAt).getTime(),
      )
      .slice(0, RISING_LIMIT)
    body =
      rising.length === 0 ? (
        <PanelEmpty>No recently corroborated stories.</PanelEmpty>
      ) : (
        <ul className="list-none">
          {rising.map((s, i) => (
            <li
              key={s.id}
              className="flex items-center gap-2.5 border-b border-border py-2 text-sm last:border-0"
            >
              <span className="w-4 shrink-0 font-mono text-[11px] text-muted-foreground">
                {i + 1}
              </span>
              {s.maxSeverity ? (
                <>
                  <span
                    className={cn(
                      'h-1.5 w-1.5 shrink-0 rounded-full',
                      SEVERITY_DOT_CLASSES[s.maxSeverity],
                    )}
                    aria-hidden="true"
                  />
                  <span className="sr-only">{s.maxSeverity} severity</span>
                </>
              ) : null}
              <Link
                to={`/stories/${s.id}`}
                className="min-w-0 flex-1 truncate font-medium text-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {s.title}
              </Link>
              <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                {s.sourceCount} src
              </span>
            </li>
          ))}
        </ul>
      )
  }
  return <RailPanel label="Rising signals">{body}</RailPanel>
}

// ─── Regulatory watch ─────────────────────────────────────────────────────────

function formatDueDate(iso: string): string {
  const parsed = new Date(iso)
  return Number.isNaN(parsed.getTime()) ? iso : format(parsed, 'd MMM')
}

function RegulatoryWatchPanel() {
  const { data, isLoading, isError } =
    useDashboardTile<RegulatoryTileResponse>('regulatory-tile')

  let body: ReactNode
  if (isLoading) {
    body = <PanelSkeleton label="regulatory watch" />
  } else if (isError || !data) {
    body = <PanelError what="regulatory watch" />
  } else if (data.mode === 'cta') {
    body = (
      <p className="py-2 text-sm text-muted-foreground">
        <Link
          to={data.cta.href}
          className="font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {data.cta.label}
        </Link>
      </p>
    )
  } else if (data.deadlines.length === 0) {
    body = <PanelEmpty>No upcoming deadlines.</PanelEmpty>
  } else {
    body = (
      <>
        <ul className="list-none">
          {data.deadlines.slice(0, DEADLINE_LIMIT).map((d) => (
            <li
              key={d.id}
              className="flex items-center gap-2.5 border-b border-border py-2 text-sm last:border-0"
            >
              <span className="shrink-0 rounded-sm bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                {d.frameworkName}
              </span>
              <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                {d.label}
              </span>
              <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                {formatDueDate(d.dueDate)}
              </span>
            </li>
          ))}
        </ul>
        <Link
          to="/regulatory/calendar"
          className="mt-2 inline-block text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Open regulatory calendar
        </Link>
      </>
    )
  }
  return <RailPanel label="Regulatory watch">{body}</RailPanel>
}

// ─── Top victim countries ─────────────────────────────────────────────────────

function VictimBars(props: { rows: Array<{ label: string; count: number }> }) {
  const max = Math.max(...props.rows.map((r) => r.count), 1)
  return (
    <ul className="list-none">
      {props.rows.map((row, i) => (
        <li key={row.label} className="flex items-center gap-2.5 py-1.5 text-sm">
          <span className="w-4 shrink-0 font-mono text-[11px] text-muted-foreground">
            {i + 1}
          </span>
          <span className="w-28 shrink-0 truncate font-medium text-foreground">
            {row.label}
          </span>
          <span
            className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-muted"
            role="presentation"
          >
            <span
              className="block h-full rounded-full bg-primary/70"
              style={{ width: `${(row.count / max) * 100}%` }}
            />
          </span>
          <span className="w-8 shrink-0 text-right font-mono text-[11px] text-muted-foreground">
            {row.count}
          </span>
        </li>
      ))}
    </ul>
  )
}

function VictimsPanel() {
  const { data, isLoading, isError } = useDashboardTile<T7Response>(
    'victim-countries-30d',
  )

  let label = 'Top victim countries 30d'
  let body: ReactNode
  if (isLoading) {
    body = <PanelSkeleton label="victim countries" />
  } else if (isError || !data) {
    body = <PanelError what="victim countries" />
  } else if (data.sufficient && data.buckets.length > 0) {
    body = (
      <VictimBars
        rows={data.buckets.slice(0, VICTIM_LIMIT).map((b) => ({
          label: displayMapper.country(b.countryIso2),
          count: b.count,
        }))}
      />
    )
  } else if (data.fallbackSectors && data.fallbackSectors.length > 0) {
    label = 'Top victim sectors 30d'
    body = (
      <VictimBars
        rows={data.fallbackSectors.slice(0, VICTIM_LIMIT).map((s) => ({
          label: displayMapper.sector(s.sector),
          count: s.count,
        }))}
      />
    )
  } else {
    body = <PanelEmpty>Insufficient signal in window.</PanelEmpty>
  }
  return <RailPanel label={label}>{body}</RailPanel>
}

// ─── Rail ─────────────────────────────────────────────────────────────────────

export interface DashboardRailProps {
  stories: StoryCardShape[] | undefined
  storiesLoading?: boolean
  storiesError?: boolean
}

export function DashboardRail(props: DashboardRailProps) {
  return (
    <div className="grid content-start gap-4 sm:grid-cols-2 xl:grid-cols-1">
      <RisingSignalsPanel
        stories={props.stories}
        isLoading={props.storiesLoading}
        isError={props.storiesError}
      />
      <RegulatoryWatchPanel />
      <VictimsPanel />
    </div>
  )
}
